import React, {useEffect, useRef, useState} from 'react';
import {useParams, useSearchParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import projectUtils from "../../../../utils/project-utils.js";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaPlus, FaTrash} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import vehicleNotesService from "../../../../services/web/vehicle-notes-service.js";
import moment from "moment";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import platePatternUtils from "../../../../utils/plate-pattern-utils.js";

const initialState = {
    description: "",
    vehicles: []
}

const VehicleNotesForm = () => {
    const {id} = useParams();
    const navigate = navigateHook();

    const [searchParams] = useSearchParams();

    const [state, setState] = useState(initialState)
    const [isLoading, setIsLoading] = useState(false);
    const [plate, setPlate] = useState("")

    const {data, refetch, isLoading: isLoadingFetch} = useQuery({
        queryFn: () => vehicleNotesService.get(id),
        queryKey: ['vehicle-note'],
        enabled: !!id
    });

    const [columnDefs,] = useState([
        { field: "plate", headerName: "Placa", width: 300, valueFormatter: ({value}) => platePatternUtils.formatPlate(value)},
        { field: "brandmodel", headerName: "Marca/Modelo", flex: 2},
        { field: "color", headerName: "Cor", flex: 1},
        {
            field: "",
            headerName: "Ação",
            width: 80,
            cellRenderer: ({data}) => {
                const onClick = () => {
                    projectUtils.swalMessage(
                        "warning",
                        "Excluir",
                        `Excluir a placa "${platePatternUtils.formatPlate(data.plate)}"?`,
                        "Sim",
                        true,
                        "Não",
                        ({isConfirmed}) => {
                            if (isConfirmed) {
                                setState(prev => ({...prev, vehicles: prev.vehicles.filter(v => v.plate !== data.plate)}))
                            }
                        }
                    );
                }

                return (
                    <section className={"h-full w-full report-center place-items-center"}>
                        <Button target={"danger"} onClick={onClick}>
                            <FaTrash />
                        </Button>
                    </section>
                )
            },
            cellRendererParams: {setState}
        },
    ]);

    const gridRef = useRef(undefined);

    useEffect(() => {
        if (!!id && data) {
            setState({description: data.description, vehicles: data.vehicles});
        } else  {
            setState(initialState);
        }
    }, [id, data]);

    const submit = () => {
        setIsLoading(true);

        const formData = new FormData();
        formData.append("note", state.description)
        formData.append("vehicles", JSON.stringify(state.vehicles))

        let response;
        if (id) {
            formData.append('_method', 'PUT');
            response = vehicleNotesService.edit(id, formData);
        }
        else response = vehicleNotesService.create(formData);

        response
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    refetch();
                    return;
                }

                const urlDev = `/vehicle-notes/form/${data.id}`;
                navigate(urlDev);
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            }).finally(() => setIsLoading(false))
    }

    const deleteNote = () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            `Excluir a anotação "${data.description ?? ""}"?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    setIsLoading(true)

                    vehicleNotesService.delete(id)
                        .then(() => {
                            projectUtils.success(true, "Excluído com sucesso");
                            navigate("/vehicle-notes");
                        })
                        .catch((error) => {
                            console.error(error);
                            let message = 'Não foi possível excluir.';
                            if (error.response.status !== 500) {
                                message = error.response.data.message;
                            }

                            projectUtils.swalErrorMessage('', message, 'Ok')
                        }).finally(() => setIsLoading(false))
                }
            }
        );
    }

    const addPlate = async (searchParamPlate = undefined) => {
        const cleanPlate = platePatternUtils.cleanRawPlate(searchParamPlate ?? plate);
        if (!platePatternUtils.existsByPlate(cleanPlate)) {
            projectUtils.swalErrorMessage('', "A placa é inválida", 'Ok');
            return;
        }

        const vehicle = {
            plate: cleanPlate.toUpperCase(),
            brandmodel: '',
            color: '',
        }

        await vehicleNotesService.getVehicleInfo(cleanPlate)
            .then(response => {
                const data = response.data.data;
                if (data) {
                    vehicle.color = data.color ?? "";
                    vehicle.brandmodel = data.brandmodel;

                    setPlate("");
                }
            });

        const vehicles = state.vehicles;
        if (vehicles.length && vehicles.filter(vehicle_ => vehicle_.plate === vehicle.plate).length) {
            projectUtils.swalErrorMessage('', "A anotação atual já foi adicionada a esse veículo", 'Ok');
            return;
        }

        setState(prev => ({...prev, vehicles: [...prev.vehicles, vehicle]}))
    }

    const onGridReady = () => {
        if (searchParams.has("plate")) {
            addPlate(searchParams.get("plate"))
        }
    }

    return (
        <Template isLoading={isLoadingFetch || isLoading}>
            <Template.Header>
                <Template.Title>Anotação de veículo</Template.Title>

                {
                    id &&
                    <Button target={"default"} onClick={() => navigate("/vehicle-notes/form")}>
                        <FaPlus /> Adicionar anotação
                    </Button>
                }
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body className={"space-y-2"}>
                        <InputForm
                            id={"description"}
                            label={"Anotação *"}
                            value={state.description}
                            onChange={({target: {value}}) => setState(prev => ({...prev, description: value}))}
                        />

                        <section className={"w-4/12 flex justify-between gap-4"}>
                            <InputForm
                                classNameWrapper={"w-full"}
                                id={"plate"}
                                label={"Placa *"}
                                value={plate}
                                onChange={({target: {value}}) => setPlate(platePatternUtils.formatPlate(value))}
                            />

                            <Button onClick={() => addPlate()} title={"Adicionar placa"}>
                                <FaPlus />
                            </Button>
                        </section>

                        <section style={{height: "calc(100vh - 550px)"}}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={state.vehicles ?? []}
                                columnDefs={columnDefs}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme({rowHeight: 37})}
                                onGridReady={onGridReady}
                            />
                        </section>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className={"flex gap-2 items-center " +(id ? "justify-between" : "justify-end")}>
                        {
                            id && data &&
                            <p className={"report-center"}>
                                Cadastrado por <b>{data.user_create}</b> em <b>{moment(data.dt_create).format("DD/MM/YYYY HH:mm")}</b>
                                {
                                    data.dt_update &&
                                    <><br/>Alterado por <b>{data.user_update}</b> em <b>{moment(data.dt_update).format("DD/MM/YYYY HH:mm")}</b></>
                                }
                            </p>
                        }

                        <section className={"flex items-center gap-2"}>
                            {
                                id &&
                                <Button target={"danger"} onClick={deleteNote} actionFeedbackType='delete'>
                                    Excluir
                                </Button>
                            }
                            <Button onClick={submit} actionFeedbackType='save'>
                                Salvar
                            </Button>
                        </section>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default VehicleNotesForm;